import React, { Component, ErrorInfo, ReactNode } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Home, Sparkles, AlertTriangle } from "lucide-react";
import { captureException as sentryCaptureException } from "@/lib/sentry-lazy";
import { supabase } from "@/integrations/supabase/client";
import { isChunkLoadError, hardResetApp } from "@/lib/chunkErrorRecovery";
import { createLogger } from '@/lib/logger';
const log = createLogger('ErrorBoundary');

/** Log error details to Supabase for production monitoring */
async function logErrorToSupabase(error: Error, errorInfo: ErrorInfo, context?: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const payload = {
      error_message: error.message,
      error_stack: error.stack?.substring(0, 2000),
      component_stack: errorInfo.componentStack?.substring(0, 2000),
      context: context || "global",
      url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: session?.user?.id || null,
      timestamp: new Date().toISOString(),
    };
    // Use rpc-style raw insert to avoid type mismatch with auto-generated types
    await (supabase as unknown as { from: (t: string) => { insert: (d: unknown) => Promise<unknown> } })
      .from("client_error_logs")
      .insert(payload);
  } catch {
    log.warn("Failed to log error to Supabase");
  }
}

interface Props {
  children: ReactNode;
  context?: string;
  fallbackMessage?: string;
  /** When true, renders a small inline error card instead of the full-screen overlay. */
  compact?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    log.error("ErrorBoundary caught an error:", error, errorInfo);
    logErrorToSupabase(error, errorInfo, this.props.context);
    // Lazy wrapper queues the error if Sentry hasn't loaded yet, then
    // replays once init completes.
    sentryCaptureException(error, {
      componentStack: errorInfo.componentStack,
      boundary: this.props.context || "global",
    });
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.compact) {
        return (
          <CompactErrorDisplay
            message={this.props.fallbackMessage}
            onReset={this.handleReset}
          />
        );
      }
      return <ErrorDisplay error={this.state.error} onRefresh={this.handleRefresh} onReset={this.handleReset} onHardReset={hardResetApp} isChunkError={isChunkLoadError(this.state.error?.message)} />;
    }

    return this.props.children;
  }
}

interface CompactErrorDisplayProps {
  message?: string;
  onReset: () => void;
}

function CompactErrorDisplay({ message, onReset }: CompactErrorDisplayProps) {
  return (
    <div
      role="alert"
      className="bg-card rounded-2xl p-5 border border-destructive/20 text-center my-3"
    >
      <div className="flex items-center justify-center gap-2 mb-2 text-destructive">
        <AlertTriangle className="w-4 h-4" aria-hidden="true" />
        <span className="text-sm font-semibold">This section couldn't load</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {message || "Something went wrong here, but the rest of the page is still working."}
      </p>
      <button
        onClick={onReset}
        className="text-xs font-medium text-primary hover:underline"
      >
        Try again
      </button>
    </div>
  );
}

interface ErrorDisplayProps {
  error: Error | null;
  onRefresh: () => void;
  onReset: () => void;
  onHardReset: () => void | Promise<void>;
  isChunkError: boolean;
}

function ErrorDisplay({ error, onRefresh, onHardReset, isChunkError }: ErrorDisplayProps) {
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6 text-center"
      style={{
        background: `
          radial-gradient(ellipse at 30% 20%, hsl(var(--primary) / 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 70% 80%, hsl(var(--accent) / 0.1) 0%, transparent 50%),
          linear-gradient(180deg, hsl(var(--background)) 0%, hsl(35 100% 97%) 100%)
        `
      }}
    >
      <motion.div 
        className="max-w-md w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Error Badge */}
        <motion.span 
          className="inline-block bg-destructive/10 text-destructive px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-widest mb-7 border border-destructive/20"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          Something Went Wrong
        </motion.span>

        {/* Icon Container */}
        <motion.div 
          className="w-28 h-28 mx-auto mb-8 rounded-full flex items-center justify-center bg-gradient-to-br from-primary/80 to-primary"
          style={{
            boxShadow: "0 20px 60px hsl(var(--primary) / 0.4)"
          }}
          animate={{ 
            scale: [1, 1.03, 1],
          }}
          transition={{ 
            duration: 3, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
        >
          <AlertTriangle className="w-12 h-12 text-white" />
        </motion.div>

        {/* Title */}
        <h1 className="font-serif text-3xl sm:text-4xl font-medium mb-4 text-foreground leading-tight">
          Oops, a little <span className="text-gradient-primary">hiccup</span>
        </h1>

        {/* Subtitle */}
        <p className="text-base sm:text-lg text-muted-foreground mb-6 font-light leading-relaxed">
          Something unexpected happened, but don't worry — every moment is a chance to reset and begin again.
        </p>

        {/* Error Card */}
        <motion.div 
          className="bg-card rounded-3xl p-6 sm:p-8 shadow-xl mb-8 relative overflow-hidden text-left"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {/* Gold top border */}
          <div className="absolute top-0 left-0 right-0 h-1 gradient-primary" />

          <p className="font-serif text-lg sm:text-xl italic text-foreground mb-4 leading-relaxed text-center">
            "Take a breath. This too shall pass."
          </p>

          {error && (
            <div className="mt-4 p-3 bg-muted/50 rounded-xl">
              <p className="text-xs font-mono text-muted-foreground break-words">
                {error.message || "An unexpected error occurred"}
              </p>
            </div>
          )}

          <p className="text-sm text-muted-foreground flex items-center justify-center gap-2 mt-4">
            <Sparkles className="w-4 h-4 text-primary" />
            Try refreshing or head back home
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <button 
            onClick={() => { void (isChunkError ? onHardReset() : onRefresh()); }}
            className="inline-flex items-center gap-2.5 gradient-primary text-white px-8 py-4 rounded-full font-semibold text-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            style={{
              boxShadow: "0 8px 30px hsl(var(--primary) / 0.4)"
            }}
          >
            <RefreshCw className="w-4 h-4" />
            {isChunkError ? "Reset App" : "Refresh Page"}
          </button>

          <a 
            href="/app/home"
            className="inline-flex items-center gap-2.5 bg-card text-foreground px-8 py-4 rounded-full font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-border"
          >
            <Home className="w-4 h-4" />
            Go Home
          </a>
        </motion.div>

        {/* Secondary Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <a 
            href="/app/practice"
            className="block mt-5 text-sm text-muted-foreground hover:text-accent transition-colors duration-300"
          >
            Or start your gratitude practice →
          </a>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default ErrorBoundary;
